import { useEffect, useRef, useState } from 'react';

type TalentReviewPlacementQuestions = {
	topic: string;
	question: string;
	answers: string[];
};

const STEPS: TalentReviewPlacementQuestions[] = [
	{
		topic: 'Prestatie',
		question: 'Hoe vaak moest jij of een collega werk van {name} herstellen of overnemen?',
		answers: ['Zelden of nooit', 'Af en toe', 'Regelmatig', 'Is niet gebeurd / Weet ik niet'],
	},
	{
		topic: 'Potentieel',
		question: 'Hoe snel pakt {name} nieuwe verantwoordelijkheden op?',
		answers: ['Langzaam', 'In een normaal tempo', 'Snel', 'Weet ik niet'],
	},
	{
		topic: 'Samenwerking',
		question: 'Hoe werkt {name} samen met collega’s in het team?',
		answers: ['Moeilijk', 'Wisselend', 'Goed', 'Weet ik niet'],
	},
];

const withName = (text: string, name: string) => text.replaceAll('{name}', name);

export default function useTalentReviewPlacement(firstName: string) {
	const stepCount = STEPS.length + 1;
	const [stepIndex, setStepIndex] = useState(0);
	const [answers, setAnswers] = useState<(string | undefined)[]>(() => STEPS.map(() => undefined));
	const isResult = stepIndex === STEPS.length;
	const advanceTimeout = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => () => clearTimeout(advanceTimeout.current), []);

	/* Answering moves on to the next step after a beat, so the choice is seen before the slide. */
	const handleSelect = (index: number, answer: string) => {
		setAnswers((current) => current.map((value, item) => (item === index ? answer : value)));
		clearTimeout(advanceTimeout.current);
		advanceTimeout.current = setTimeout(() => {
			setStepIndex((current) => (current === index ? Math.min(stepCount - 1, index + 1) : current));
		}, 200);
	};

	const handlePrevious = () => {
		setStepIndex((index) => Math.max(0, index - 1));
	};

	const canGoNext = isResult || !!answers[stepIndex];

	/* A step can be opened directly once every question before it is answered. */
	const canGoTo = (index: number) =>
		index >= 0 && index < stepCount && answers.slice(0, index).every((answer) => !!answer);

	const handleGoTo = (index: number) => {
		if (!canGoTo(index)) {
			return;
		}

		setStepIndex(index);
	};

	const handleNext = () => {
		if (!canGoNext) {
			return;
		}

		setStepIndex((index) => Math.min(stepCount - 1, index + 1));
	};

	return {
		stepIndex,
		stepCount,
		questions: STEPS.map((step, index) => ({
			topic: step.topic,
			question: withName(step.question, firstName),
			options: step.answers,
			selected: answers[index],
		})),
		isFirst: stepIndex === 0,
		isLast: stepIndex === stepCount - 1,
		isResult,
		canGoNext,
		canGoTo,
		handleGoTo,
		handleSelect,
		handlePrevious,
		handleNext,
	};
}
